import type { IAccountDataResponse, IBetLog } from '@hit-game/shared/types';
import { Loader2 } from 'lucide-react';

import { useSyncedHistory } from '../../hooks/useSyncedHistory';
import { accountService } from '../../services/account.service';
import { betLogService } from '../../services/betlog.service';
import { formatBetDescription, formatDateTime, formatMoney } from '../../utils/format';
import { Modal } from '../common/Modal';
import { SyncingBadge } from './SyncingBadge';

/** Server chỉ giữ 30 bản ghi mỗi tài khoản sau khi dọn — xin đúng bằng đó. */
const PAGE_SIZE = 30;

interface BetLogModalProps {
  account: IAccountDataResponse | null;
  onClose: () => void;
}

/**
 * Lịch sử cược — mở ra là hiện bản trong DB ngay, rồi tự kéo bản mới từ nhà cái.
 *
 * Phần đã lưu luôn xem được kể cả khi tài khoản mất phiên: đồng bộ hỏng thì bảng
 * giữ nguyên chứ không trống đi.
 */
export function BetLogModal({ account, onClose }: BetLogModalProps) {
  const accountId = account ? String(account._id) : null;

  const { items, loading, syncing } = useSyncedHistory<IBetLog>({
    accountId,
    load: async (id) => (await betLogService.list(id, { page: 1, limit: PAGE_SIZE })).items,
    sync: (id) => accountService.syncBetLogs(id),
  });

  return (
    <Modal
      open={account !== null}
      onClose={onClose}
      maxWidth={880}
      title={
        <span className="flex items-center gap-2">
          Lịch sử cược
          <span className="text-ink-dim text-[12px] font-normal">{account?.username}</span>
          <SyncingBadge show={syncing} />
        </span>
      }
    >
      {loading ? (
        <div className="text-ink-dim flex items-center justify-center gap-2 py-10 text-[13px]">
          <Loader2 size={16} className="animate-spin" />
          Đang tải...
        </div>
      ) : items.length === 0 ? (
        <div className="text-ink-dim space-y-2 py-10 text-center text-[13px]">
          <p className="font-semibold">Chưa có lịch sử cược</p>
          <p className="text-ink-faint text-[11px]">
            {syncing
              ? 'Đang kéo dữ liệu từ nhà cái...'
              : 'Nhà cái chỉ ghi lịch sử khi tài khoản đã đăng nhập qua tool (tool tự bật lưu 1 giờ sau mỗi lần đăng nhập). Ván đánh trước đó không lấy lại được.'}
          </p>
        </div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full border-collapse text-left">
            <thead className="bg-canvas-raised sticky top-0">
              <tr>
                {[
                  'Thời gian',
                  'Game',
                  'Nội dung',
                  'Cược',
                  'Thắng/Thua',
                  'Số dư sau',
                  'Mã phiếu',
                ].map((column) => (
                  <th
                    key={column}
                    className="text-ink-faint px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={String(item._id)} className="border-line hover:bg-panel-hover border-t">
                  <td className="text-ink-muted whitespace-nowrap px-3 py-2 font-mono text-[11px]">
                    {formatDateTime(item.createdTime)}
                  </td>
                  <td className="text-ink-dim px-3 py-2 text-[11px]">{item.serviceName}</td>
                  <td className="text-ink px-3 py-2 text-[11px]">
                    {item.description ? formatBetDescription(item.description) : '—'}
                  </td>
                  <td className="text-ink-muted px-3 py-2 text-right font-mono text-[12px]">
                    {formatMoney(item.stake)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono text-[12px] font-bold ${
                      item.exchangeValue > 0
                        ? 'text-success'
                        : item.exchangeValue < 0
                          ? 'text-danger'
                          : 'text-ink-dim'
                    }`}
                  >
                    {item.exchangeValue > 0 ? '+' : ''}
                    {formatMoney(item.exchangeValue)}
                  </td>
                  <td className="text-ink-muted px-3 py-2 text-right font-mono text-[12px]">
                    {formatMoney(item.closingValue)}
                  </td>
                  {/* Mã phiếu nhà cái — thứ duy nhất tra cứu được bên HIT khi đối soát.
                      `select-all` để bấm một cái là chọn trọn 12 chữ số, khỏi bôi tay. */}
                  <td className="text-ink-faint select-all whitespace-nowrap px-3 py-2 font-mono text-[11px]">
                    {item.betId}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
